'use client';

import { useOrgTrialUsageLimit } from '@/@studio-ai/hooks/useOrgTrialUsageLimit';
import { useSession } from '@/providers/enhanced-session';
import { Link } from '@heroui/react';
import { useEffect, useState } from 'react';

// Only warn when the trial end is close enough to be actionable
const TRIAL_WARNING_THRESHOLD_DAYS = 14;

// Key is scoped to the calendar day so the banner reappears the next day if not yet acted on
const getDismissalKey = () => `trial_banner_dismissed_${new Date().toISOString().slice(0, 10)}`;

export const TrialNotificationBanner = () => {
    const { data: session } = useSession();
    // Reads from the access-status endpoint — the single source of truth for org trial access
    const usageLimit = useOrgTrialUsageLimit();
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        setIsDismissed(localStorage.getItem(getDismissalKey()) === 'true');
    }, []);

    const daysRemaining = usageLimit?.daysUntilReset ?? null;
    const hasReachedLimit = usageLimit?.hasReachedLimit ?? false;

    // No data or stale session after expiry
    if (daysRemaining === null || daysRemaining < 0) return null;
    // Trial ended today and messages are exhausted — nothing left to warn about
    if (daysRemaining === 0 && hasReachedLimit) return null;
    // Too far out to be worth surfacing
    if (daysRemaining > TRIAL_WARNING_THRESHOLD_DAYS) return null;
    if (isDismissed) return null;

    const timeLabel =
        daysRemaining === 0 ? 'today' : daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
    const firstName = session?.user?.givenName || session?.user?.name?.split(' ')[0] || 'there';

    const handleDismiss = () => {
        localStorage.setItem(getDismissalKey(), 'true');
        setIsDismissed(true);
    };

    return (
        <div className="flex items-center justify-center gap-2 border-b border-primary-200 bg-primary-50 px-4 py-2 text-sm text-primary-800">
            <span>
                Hey {firstName}! 👋 You have <strong>{timeLabel}</strong> left in your trial period.{' '}
                <Link
                    href="https://www.mfour.com/contact-us/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline font-semibold underline"
                    size="sm"
                >
                    Go pro to keep access to DANI →
                </Link>
            </span>
            <button
                onClick={handleDismiss}
                className="ml-2 text-primary-600 hover:text-primary-800"
                aria-label="Dismiss"
            >
                ×
            </button>
        </div>
    );
};
